import { useState } from "react";
import { X } from "lucide-react";

import { toast } from "@/components/ui/use-toast";
import {
  reportContent,
  ReportReason,
  ReportTarget,
} from "@/lib/safety";

const REPORT_REASONS: Array<{ label: string; value: ReportReason }> = [
  { label: "Spam", value: "spam" },
  { label: "Inappropriate", value: "inappropriate" },
  { label: "Harassment", value: "harassment" },
  { label: "Fraud", value: "fraud" },
  { label: "Other", value: "other" },
];

type ReportDialogProps = {
  open: boolean;
  targetType: ReportTarget;
  targetId: string;
  onClose: () => void;
};

export default function ReportDialog({
  open,
  targetType,
  targetId,
  onClose,
}: ReportDialogProps) {
  const [submitting, setSubmitting] = useState<ReportReason | null>(null);

  if (!open) return null;

  const submit = async (reason: ReportReason) => {
    setSubmitting(reason);
    try {
      await reportContent(targetType, targetId, reason);
      onClose();
      toast({
        title: "Report received",
        description: "Thank you. Busiconn will review this report.",
      });
    } catch (error) {
      toast({
        title: "Report not sent",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-dialog-title"
        className="w-full max-w-sm rounded-xl bg-white p-5 text-gray-900 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 id="report-dialog-title" className="text-lg font-semibold">Report reason</h2>
            <p className="mt-1 text-sm text-gray-600">Choose the closest reason.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close report dialog"
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {REPORT_REASONS.map((reason) => (
            <button
              key={reason.value}
              type="button"
              disabled={submitting !== null}
              onClick={() => void submit(reason.value)}
              className="w-full rounded-lg bg-gray-100 px-4 py-3 font-medium hover:bg-gray-200 disabled:opacity-60"
            >
              {submitting === reason.value ? "Sending..." : reason.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
